import React, { useCallback, useEffect, useState } from "react";
import ModalWrapper from "..";
import { Button } from "../../button";
import toast from "react-hot-toast";
import * as Yup from "yup";
import useFormWithSchema from "../../../hooks/useFormWithSchema";
import { Controller } from "react-hook-form";
import TextInput from "../../inputs/TextInput";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useRequestMoreInfoFromStaff } from "../../../hooks/visitor/mutation";
import DeviceApprovalDetailsTable from "../../table/DeviceApprovalDetailsTable";
import {
  Device,
  DEVICES_APPROVE_REQUEST_DETAILS_TABLE,
  STATUS_IDS,
  USER_ROLES_IDS,
} from "../../../constants/common";
import { cn } from "../../../utils/cn";
import useAuthStore from "../../../store/Auth";
import { APP_ROUTES } from "../../../routes";

interface RequestMoreInfoModalProps {
  appointmentId?: number;
  isOpen: boolean;
  onOpenChange: () => void;
  addedDevices?: Device[];
  persistedDevices?: Device[];
  isDevicesApproval: boolean;
  appointmentDateTime: {
    visitStartDate?: string;
    visitEndDate?: string;
  };
}

const RequestMoreInfoModalSchema = Yup.object({
  comments: Yup.string(),
});

interface RequestMoreInfoModalData {
  comments?: string;
}

const RequestMoreInfoModal = ({
  appointmentId,
  isOpen,
  onOpenChange,
  addedDevices,
  isDevicesApproval,
  appointmentDateTime,
  persistedDevices,
}: RequestMoreInfoModalProps) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useFormWithSchema(RequestMoreInfoModalSchema);

  const { t } = useTranslation();

  const navigate = useNavigate();

  const user = useAuthStore((state) => state.user);

  const {
    mutateAsync: RequestMoreInfoFromStaff,
    isPending: isRequestMoreInfoLoading,
  } = useRequestMoreInfoFromStaff();

  const [devices, setDevices] = useState(addedDevices);

  useEffect(() => {
    if (addedDevices) {
      setDevices(addedDevices);
    }
  }, [addedDevices]);

  const onSubmit = useCallback(
    async (data: RequestMoreInfoModalData) => {
      if (!appointmentId) return;

      const isRequestedInfoDeviceCommentMissing = devices?.some(
        (item) => item.status === STATUS_IDS.REQUEST_INFO && !item.comment
      );

      if (isRequestedInfoDeviceCommentMissing) {
        toast.error(t("provideCommentsAllRequestedInfoDevices"));
        return;
      }

      const devicesStatusUpdated = devices?.filter((item) => item.status);

      const isNonApprovedDevicesSelected =
        devices?.filter(
          (item) =>
            item.currentUserDeviceStatus !== STATUS_IDS.APPROVED && !item.status
        ).length === 0;

      const isAllDevicesApproved = devicesStatusUpdated?.every(
        (item) => item.status === STATUS_IDS.APPROVED
      );

      if (
        devices &&
        devices?.length > 1 &&
        isDevicesApproval &&
        !isNonApprovedDevicesSelected
      ) {
        toast.error(t("approveRequestInformationAllDevices"));
        return;
      } else if (
        ((devices &&
          (devicesStatusUpdated?.length === 0 || isAllDevicesApproved)) ||
          !devices) &&
        !data.comments
      ) {
        toast(t("noInformationRequested"), {
          icon: "⚠️",
        });
        return;
      }

      try {
        const body = {
          encryptedId: appointmentId.toString(),
          n_RoleID: user?.n_RoleID,
          s_VisitComment: data.comments,

          dt_AppointmentStartTime: appointmentDateTime?.visitStartDate,
          dt_AppointmentEndTime: appointmentDateTime?.visitEndDate,

          n_IsApprovalDevicesUpdate: isDevicesApproval ? 1 : 0,
          N_IsHostRequiredMoreInfo:
            user?.n_RoleID === USER_ROLES_IDS.STAFF ? 1 : 0,
          approvalDevices:
            (user?.n_RoleID !== USER_ROLES_IDS.STAFF
              ? devices
              : persistedDevices
            )?.map((item) => ({
              appointmentDeviceId: item.appointmentDeviceId,
              s_Comment: item.status ? item.comment : "",
              n_ApprovalStatus: item.approvalStatus || 0,
              n_CurrentApprovalStatus:
                item.status || item.currentUserDeviceStatus || 0,
            })) || [],
        };

        await RequestMoreInfoFromStaff(body);
        navigate(APP_ROUTES.APPROVE_VISIT_REQUEST);
        toast.success(t("informationRequestedSuccessfully"));
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      } finally {
        onOpenChange();
      }
    },
    [
      persistedDevices,
      RequestMoreInfoFromStaff,
      appointmentDateTime?.visitEndDate,
      appointmentDateTime?.visitStartDate,
      appointmentId,
      devices,
      isDevicesApproval,
      navigate,
      onOpenChange,
      t,
      user?.n_RoleID,
    ]
  );

  const onClose = useCallback(() => {
    setDevices?.((prev?: Device[]) =>
      prev?.map((device: Device) => ({ ...device, status: STATUS_IDS.SELECT }))
    );
    reset({ comments: "" });
    onOpenChange();
  }, [onOpenChange, reset]);

  return (
    <ModalWrapper
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      contentStyles="lg:max-w-[90vw]"
    >
      <div className="max-h-[70vh] overflow-y-auto w-full pb-1">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-5 justify-center items-center w-full"
        >
          <p className="text-lg font-semibold mx-auto">
            {t("requestInformation")}
          </p>

          <div className="w-[80vw] lg:w-[70vw]">
            {addedDevices?.length &&
              addedDevices?.length > 0 &&
              isDevicesApproval && (
                <DeviceApprovalDetailsTable
                  devices={devices}
                  type={
                    DEVICES_APPROVE_REQUEST_DETAILS_TABLE.REQUEST_DETAILS_TABLE
                  }
                  setDevices={setDevices}
                />
              )}
          </div>

          <div
            className={cn(
              "flex flex-col gap-3 w-3/4",
              !isDevicesApproval && "-mt-4"
            )}
          >
            <Controller
              name="comments"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <TextInput
                  label={t("comments")}
                  onChange={onChange}
                  error={errors?.[name]?.message}
                  value={value}
                />
              )}
            />
          </div>

          <div className="flex gap-2 items-center">
            <Button isLoading={isRequestMoreInfoLoading}>
              {t("requestInfo")}
            </Button>
            <Button
              onClick={onClose}
              className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
            >
              {t("close")}
            </Button>
          </div>
        </form>
      </div>
    </ModalWrapper>
  );
};

export default React.memo(RequestMoreInfoModal);
